import { baseVariables, toRem } from '@marriott/mi-ui-library';
import styled, { keyframes } from 'styled-components';

/**
 * WEB-196460: homepage-scoped skeleton shimmer that animates `transform` (compositor-only).
 *
 * The shared design-system shimmer (global-styles `.skeleton-loader::after`,
 * `@keyframes loading`) animates `left`/`top` — layout properties — so Chrome's Layout
 * Instability API books a layout shift every 900ms cycle for as long as a skeleton is
 * visible. On Polaris that's catastrophic CLS: the DCA card skeletons (PDC/CoBrand/Riott)
 * sit large and in-fold and wait ~3s on Adobe Target. That CSS ships as a version-pinned,
 * separately-published asset (global-styles/<pkg>/marriot.global.min.css) the homepage
 * deploy cannot change, so we override it HERE — in the homepage app bundle, scoped to the
 * DCA grid — re-pointing the pseudo's animation to a transform sweep. Same visual, zero
 * layout shift, homepage-squad-only blast radius. translate() %s are relative to the
 * pseudo's own 300% size (matched to reproduce the original diagonal sweep).
 */
const skeletonShimmerTransform = keyframes`
  0% {
    transform: translate(-80%, -80%);
  }
  50% {
    transform: translate(-56.67%, -56.67%);
  }
  100% {
    transform: translate(-13.33%, -13.33%);
  }
`;

export const StyledDynamicTabbedLayout = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: auto;

  @media screen and (max-width: 576px) {
    & .container {
      max-width: 100vw;
    }
  }
`;

/**
 * Dynamic Tab Panel grid (3-column responsive layout with spacing)
 * Used by DynamicTabbedLayout to render the slots area beneath the banner and tabs.
 */
export const StyledTabPanelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  grid-auto-flow: dense;
  gap: ${toRem(24)};
  margin-top: ${toRem(32)};
  @media ${baseVariables.mediaQuery.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${baseVariables.mediaQuery.lg} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  /* WEB-196460: override the global left/top shimmer with a transform sweep for every
     skeleton inside the DCA grid. !important on the longhand beats the global \`animation\`
     shorthand; left/top are pinned so the now-unused global keyframe leaves no offset. */
  & .skeleton-loader::after {
    left: 0 !important;
    top: 0 !important;
    animation-name: ${skeletonShimmerTransform} !important;
  }
`;

/**
 * Each grid item – className controls column/row behavior:
 * - .pos-1 | .pos-2 | .pos-3 → place non-banner items across columns
 * - .banner → spans all three columns
 * - .tall   → spans two rows in height
 * - .square → single row height
 */
export const StyledTabPanelItem = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  /* Ensure inner cards can shrink within the grid item on small screens */
  > * {
    min-width: 0;
    width: 100%;
  }

  @media ${baseVariables.mediaQuery.lg} {
    &.pos-1 {
      grid-column: 1;
    }
    &.pos-2 {
      grid-column: 2;
    }
    &.pos-3 {
      grid-column: 3;
    }
  }

  &.banner {
    /* Span all available columns across breakpoints */
    grid-column: 1 / -1;
    grid-row: span 1;
  }
  &.tall {
    grid-row: span 1;
    @media ${baseVariables.mediaQuery.lg} {
      grid-row: span 2;
    }
  }
  &.square {
    grid-row: span 1;
  }

  .riott-media_border_radius {
    img {
      border-radius: 16px;
    }
  }
`;
